import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {boolean} */
  #isSyncing = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    
    // Setup variant syncing if enabled
    this.#setupVariantSync();
    
    // Sync fieldsets on page load if there are already selected values
    this.#syncOnLoad();
  }

  /**
   * Sets up variant syncing between multiple fieldsets within the same variant picker.
   * This is separate from the buy-tabs sync which syncs between different variant pickers on tab switch.
   * When a variant option is selected in one fieldset, it syncs to other fieldsets with matching values.
   */
  #setupVariantSync() {
    // Check if variant fieldset sync is enabled for this picker
    // It can be enabled via data attribute on the picker or its parent container
    const syncEnabled = 
      this.dataset.syncVariantFieldsets === 'true' ||
      this.closest('[data-sync-variant-fieldsets="true"]') !== null;
    
    if (!syncEnabled) return;

    // Check if this picker has multiple fieldsets (only sync if there are multiple)
    const fieldsets = this.querySelectorAll('fieldset.variant-option');
    if (fieldsets.length < 2) return; // No need to sync if there's only one fieldset

    // Listen for change events to sync based on selected option values within the same picker
    this.addEventListener('change', this.#handleChangeSync.bind(this), true);
  }

  /**
   * Syncs fieldsets on page load based on currently selected values.
   * This ensures fieldsets are in sync when the page first loads.
   */
  #syncOnLoad() {
    // Check if variant fieldset sync is enabled (NOT buy-tabs sync)
    const syncEnabled = 
      this.dataset.syncVariantFieldsets === 'true' ||
      this.closest('[data-sync-variant-fieldsets="true"]') !== null;
    
    if (!syncEnabled) return;

    // Check if this picker has multiple fieldsets
    const fieldsets = this.querySelectorAll('fieldset.variant-option');
    if (fieldsets.length < 2) return;

    // Wait a bit for DOM to be fully ready, then sync
    setTimeout(() => {
      this.#syncFieldsetsWithinPicker(this, null);
    }, 100);
  }

  /**
   * Handles syncing when a variant option changes within the same variant picker.
   * This syncs fieldsets within the same picker based on matching option values (e.g., "03 Beige Idéal").
   * 
   * NOTE: This is SEPARATE from the buy-tabs sync functionality which handles syncing between 
   * different variant pickers when switching tabs. That functionality is in buy-tabs.js.
   * 
   * This function ONLY syncs between multiple fieldsets within the SAME variant-picker element.
   * @param {Event} event - The change event
   */
  #handleChangeSync(event) {
    // Skip if this is already a sync event from buy-tabs or our own sync
    // buy-tabs.js uses __isSync flag to prevent infinite loops
    const isSyncEvent = /** @type {any} */ (event).__isSync === true;
    if (this.#isSyncing || isSyncEvent) return;
    
    // Only sync on actual user selections within this picker, not during programmatic updates
    const eventTarget = event.target;
    if (!(eventTarget instanceof HTMLElement)) return;
    
    // Make sure the change happened within this variant picker
    if (!this.contains(eventTarget)) return;
    
    const target = eventTarget;
    const isVariantInput = 
      (target instanceof HTMLInputElement && target.type === 'radio') ||
      (target instanceof HTMLSelectElement);
    
    if (!isVariantInput) return;

    // Get the value that just changed
    let changedValue = null;
    if (target instanceof HTMLInputElement) {
      changedValue = target.value;
    } else if (target instanceof HTMLSelectElement) {
      changedValue = target.value;
    }

    if (!changedValue) return;

    // Sync synchronously so that selectedOptionsValues is correct
    // before variantChanged fires and builds the fetch URL
    this.#syncFieldsetsWithinPicker(this, changedValue);
  }


  /**
   * Syncs fieldsets within the same variant picker by matching option values.
   * @param {HTMLElement} picker - The variant picker element
   * @param {string|null} changedValue - The specific value that was just changed (if provided, only syncs this value)
   */
  #syncFieldsetsWithinPicker(picker, changedValue = null) {
    const fieldsets = picker.querySelectorAll('fieldset.variant-option');
    if (fieldsets.length < 2) return; // Need at least 2 fieldsets to sync

    // If a specific value was changed, only sync that value
    if (changedValue) {
      fieldsets.forEach(targetFieldset => {
        // Find matching radio button by value
        const match = targetFieldset.querySelector(`input[type="radio"][value="${CSS.escape(changedValue)}"]`);
        if (match instanceof HTMLInputElement && !match.checked) {
          match.checked = true;
        }

        // Also try select dropdowns
        const targetSelect = targetFieldset.querySelector('select');
        if (targetSelect instanceof HTMLSelectElement && targetSelect.value !== changedValue) {
          const option = Array.from(targetSelect.options).find(
            opt => opt.value === changedValue && !opt.disabled
          );
          if (option) {
            targetSelect.value = changedValue;
          }
        }

        // Update swatch value span in the legend
        const swatchValueSpan = targetFieldset.querySelector('legend .variant-option__swatch-value');
        if (swatchValueSpan && changedValue) {
          swatchValueSpan.textContent = changedValue;
        }
      });
      return;
    }

    // Otherwise, sync all checked fieldsets (for initial sync or when no specific value is provided)
    fieldsets.forEach(sourceFieldset => {
      // Find checked input in this fieldset
      const checkedInput = sourceFieldset.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
      const checkedSelect = sourceFieldset.querySelector('select');
      
      let sourceValue = null;
      let isRadio = false;
      let isSelect = false;

      if (checkedInput instanceof HTMLInputElement) {
        sourceValue = checkedInput.value;
        isRadio = true;
      } else if (checkedSelect instanceof HTMLSelectElement && checkedSelect.value) {
        sourceValue = checkedSelect.value;
        isSelect = true;
      }

      if (!sourceValue) return;

      // Sync to all other fieldsets within the same picker
      fieldsets.forEach(targetFieldset => {
        if (targetFieldset === sourceFieldset) return; // Skip self

        if (isRadio) {
          // Find matching radio button by value
          const match = targetFieldset.querySelector(`input[type="radio"][value="${CSS.escape(sourceValue)}"]`);
          if (match instanceof HTMLInputElement && !match.checked) {
            match.checked = true;
          }
        } else if (isSelect) {
          // Find matching select dropdown
          const targetSelect = targetFieldset.querySelector('select');
          if (targetSelect instanceof HTMLSelectElement && targetSelect.value !== sourceValue) {
            // Check if the option exists and is enabled
            const option = Array.from(targetSelect.options).find(
              opt => opt.value === sourceValue && !opt.disabled
            );
            if (option) {
              targetSelect.value = sourceValue;
            }
          }
        }

        // Update swatch value span in the legend
        const swatchValueSpan = targetFieldset.querySelector('legend .variant-option__swatch-value');
        if (swatchValueSpan && sourceValue) {
          swatchValueSpan.textContent = sourceValue;
        }
      });
    });
  }

  /**
   * Syncs selected options from source picker to target picker.
   * @param {HTMLElement} sourcePicker - The source variant picker
   * @param {HTMLElement} targetPicker - The target variant picker to sync to
   * @param {NodeList} selectedInputs - Selected radio/checkbox inputs from source
   * @param {NodeList} selects - Select elements from source
   */
  #syncPickerToTarget(sourcePicker, targetPicker, selectedInputs, selects) {
    // Skip price updates during sync to prevent interference
    const isInSubscriptionTab = targetPicker.closest('.tab__content')?.querySelector('subscription-plans-picker') !== null;
    if (isInSubscriptionTab) {
      targetPicker.dataset.skipPriceUpdate = 'true';
    }

    // Sync radio/checkbox inputs by matching option values
    selectedInputs.forEach(sourceInput => {
      if (!(sourceInput instanceof HTMLInputElement)) return;

      const sourceValue = sourceInput.value;
      if (!sourceValue) return;

      // Find matching input in target by value (option name like "03 Beige Idéal")
      // Match within the same option group (fieldset) for multi-option products
      const sourceFieldset = sourceInput.closest('fieldset.variant-option');
      let targetFieldset = null;

      if (sourceFieldset) {
        // Try to find matching fieldset by legend text (option name)
        const sourceLegend = sourceFieldset.querySelector('legend');
        const sourceOptionName = sourceLegend?.textContent?.split('\n')[0]?.trim();
        
        if (sourceOptionName) {
          const targetFieldsets = targetPicker.querySelectorAll('fieldset.variant-option');
          for (const fieldset of targetFieldsets) {
            const targetLegend = fieldset.querySelector('legend');
            const targetOptionName = targetLegend?.textContent?.split('\n')[0]?.trim();
            if (targetOptionName === sourceOptionName) {
              targetFieldset = fieldset;
              break;
            }
          }
        }

        // Fallback: match by fieldset index
        if (!targetFieldset) {
          const sourceIndex = Array.from(sourcePicker.querySelectorAll('fieldset.variant-option')).indexOf(sourceFieldset);
          const targetFieldsets = targetPicker.querySelectorAll('fieldset.variant-option');
          if (sourceIndex >= 0 && sourceIndex < targetFieldsets.length) {
            targetFieldset = targetFieldsets[sourceIndex];
          }
        }
      }

      // Find matching input by value within the appropriate fieldset
      const searchScope = targetFieldset || targetPicker;
      const targetInput = searchScope.querySelector(`input[value="${CSS.escape(sourceValue)}"]`);
      
      if (targetInput instanceof HTMLInputElement && !targetInput.checked) {
        // Check if option is available (not disabled)
        const isAvailable = targetInput.dataset.optionAvailable !== 'false';
        if (isAvailable) {
          targetInput.checked = true;
          const changeEvent = new Event('change', { bubbles: true });
          /** @type {any} */ (changeEvent).__isSync = true;
          targetInput.dispatchEvent(changeEvent);
        }
      }
    });

    // Sync select dropdowns by matching option values
    selects.forEach(sourceSelect => {
      if (!(sourceSelect instanceof HTMLSelectElement)) return;

      const sourceValue = sourceSelect.value;
      if (!sourceValue) return;

      // Find matching select in target picker (by name or position)
      const selectName = sourceSelect.name;
      let targetSelect = null;

      if (selectName) {
        targetSelect = targetPicker.querySelector(`select[name="${CSS.escape(selectName)}"]`);
      } else {
        // Fallback: match by index if no name
        const sourceIndex = Array.from(sourcePicker.querySelectorAll('select')).indexOf(sourceSelect);
        const targetSelects = targetPicker.querySelectorAll('select');
        if (sourceIndex >= 0 && sourceIndex < targetSelects.length) {
          targetSelect = targetSelects[sourceIndex];
        }
      }

      if (targetSelect instanceof HTMLSelectElement && targetSelect.value !== sourceValue) {
        // Check if the option exists and is enabled
        const option = Array.from(targetSelect.options).find(
          opt => opt.value === sourceValue && !opt.disabled
        );
        if (option) {
          targetSelect.value = sourceValue;
          const changeEvent = new Event('change', { bubbles: true });
          /** @type {any} */ (changeEvent).__isSync = true;
          targetSelect.dispatchEvent(changeEvent);
        }
      }
    });

    // Update swatch value spans in legends
    const sourceFieldsets = sourcePicker.querySelectorAll('fieldset.variant-option');
    sourceFieldsets.forEach(/** @param {Element} sourceFieldset */ (sourceFieldset) => {
      const sourceCheckedInput = sourceFieldset.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
      if (!(sourceCheckedInput instanceof HTMLInputElement)) return;

      const sourceValue = sourceCheckedInput.value;
      if (!sourceValue) return;

      // Find matching fieldset in target
      const sourceLegend = sourceFieldset.querySelector('legend');
      const sourceOptionName = sourceLegend?.textContent?.split('\n')[0]?.trim();
      let targetFieldset = null;

      if (sourceOptionName) {
        const targetFieldsets = targetPicker.querySelectorAll('fieldset.variant-option');
        for (const fieldset of targetFieldsets) {
          const targetLegend = fieldset.querySelector('legend');
          const targetOptionName = targetLegend?.textContent?.split('\n')[0]?.trim();
          if (targetOptionName === sourceOptionName) {
            targetFieldset = fieldset;
            break;
          }
        }
      }

      if (targetFieldset) {
        // Update the swatch value span in the legend
        const swatchValueSpan = targetFieldset.querySelector('legend .variant-option__swatch-value');
        if (swatchValueSpan) {
          swatchValueSpan.textContent = sourceValue;
        }
      }
    });

    // Clear skip price update flag after sync completes
    if (targetPicker.dataset.skipPriceUpdate) {
      setTimeout(() => {
        delete targetPicker.dataset.skipPriceUpdate;
      }, 200);
    }
  }

  /**
   * Checks if a component exists in the same parent block.
   * @param {string} componentName - The component name to check (e.g., 'subscription-plans-picker' or 'product-packs-component').
   * @returns {boolean} True if the component is found in the block, false otherwise.
   */
  checkVariantChangeSource(componentName) {
    const blockId = this.dataset.blockId;
    if (!blockId) return false;

    // Find the parent block container
    const blockContainer = this.parentElement;
    const component = blockContainer?.querySelector(componentName);
    
    return component instanceof HTMLElement;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    
    // Mark VariantSelectedEvent if this is a sync operation to prevent recursive syncing
    const variantSelectedEvent = new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' });
    const isSyncEvent = /** @type {any} */ (event).__isSync === true;
    if (isSyncEvent) {
      /** @type {any} */ (variantSelectedEvent.detail).__isSync = true;
    }
    this.dispatchEvent(variantSelectedEvent);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Check if price update should be skipped (e.g., when syncing from subscription tab)
    // Note: The flag is cleared by the sync code after all variant syncing completes
    const shouldSkipPriceUpdate = this.dataset.skipPriceUpdate === 'true';

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // Skip fetchUpdatedSection if price update should be prevented
    if (!shouldSkipPriceUpdate) {
      this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);
    }

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Disables all variant inputs and selects while fetching.
   */
  #disableVariantInputs() {
    const inputs = this.querySelectorAll('input[type="radio"], input[type="checkbox"], select');
    inputs.forEach((input) => {
      if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement) {
        input.disabled = true;
        input.setAttribute('aria-busy', 'true');
      }
    });
  }

  /**
   * Enables all variant inputs and selects after fetching completes.
   */
  #enableVariantInputs() {
    const inputs = this.querySelectorAll('input[type="radio"], input[type="checkbox"], select');
    inputs.forEach((input) => {
      if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement) {
        input.disabled = false;
        input.removeAttribute('aria-busy');
      }
    });
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // Disable variant inputs while fetching
    this.#disableVariantInputs();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) {
          // Re-enable inputs if no content found
          this.#enableVariantInputs();
          return;
        }

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            // Check if variant change is happening in subscription or bundles context
            const hasSubscriptionWidget = this.checkVariantChangeSource('subscription-plans-picker');
            const hasProductBundles = this.checkVariantChangeSource('product-packs-component');
            
            // Create event data object with additional context information
            const eventData = {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
              hasSubscriptionWidget,
              hasProductBundles,
            };
            
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, eventData)
            );
          }
        }

        // Re-enable inputs after morph completes
        // Use requestAnimationFrame to ensure DOM updates are complete
        requestAnimationFrame(() => {
          this.#enableVariantInputs();
        });
      })
      .catch((error) => {
        // Re-enable inputs on error (unless aborted)
        if (error.name !== 'AbortError') {
          console.error(error);
          this.#enableVariantInputs();
        } else {
          // If aborted, the new fetch will handle enabling/disabling
          // But we should still enable in case this was the last fetch
          this.#enableVariantInputs();
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
